import React, { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo } from 'react'

import { ReconciliationSuggestion } from '@modules-deprecated/bankReconciliation/types'

import { EmberEventFn } from '../../../../../types/emberEventFn'

interface ContextState {
  matchSuggestionsLoaded: boolean
  matchSuggestions: ReconciliationSuggestion[]
  removeSuccessfullyMatched: (successfullyMatched: string[]) => void
  totalMatchSuggestions: number
}

const AcceptAllMatchSuggestionsContext = createContext<ContextState | undefined>(undefined)

interface AcceptAllMatchSuggestionsContextProps {
  areMatchesLoaded: string
  children?: ReactNode
  matchSuggestions: string
  removeMatches?: EmberEventFn
}

export const AcceptAllMatchSuggestionsContextProvider = ({
  children,
  areMatchesLoaded,
  matchSuggestions,
  removeMatches,
}: AcceptAllMatchSuggestionsContextProps): ReactElement => {
  const matchSuggestionsParsed: ReconciliationSuggestion[] = useMemo(
    () => matchSuggestions && JSON.parse(matchSuggestions),
    [matchSuggestions],
  )
  const matchSuggestionsLoaded = areMatchesLoaded === 'true'
  const totalMatchSuggestions = matchSuggestionsParsed?.length || 0

  const removeSuccessfullyMatched = useCallback(
    (successfullyMatched: string[]) => {
      removeMatches?.({ detail: { matches: successfullyMatched } })
    },
    [removeMatches],
  )

  return (
    <AcceptAllMatchSuggestionsContext.Provider
      value={{
        matchSuggestionsLoaded,
        matchSuggestions: matchSuggestionsParsed,
        removeSuccessfullyMatched,
        totalMatchSuggestions,
      }}
    >
      {children}
    </AcceptAllMatchSuggestionsContext.Provider>
  )
}

export const useAcceptAllMatchSuggestions = () => {
  const context = useContext(AcceptAllMatchSuggestionsContext)

  if (!context) {
    throw new Error('AcceptAllMatchSuggestionsContextProvider is missing in the module!')
  }

  return context
}
